// src/services/salesService.ts
import api from "../utils/api";

export type SalesQueryPeriod = "all" | "this_month" | "last_month" | "last_3_months" | "last_6_months" | "this_year" | "custom";

export type SalesSeriesInterval = "monthly" | "daily";

export interface SalesQueryParams {
	period?: SalesQueryPeriod;
	startDate?: string;
	endDate?: string;
	limit?: number;
	interval?: SalesSeriesInterval;
}

export interface SalesSummary {
	totalSales: number;
	monthlySales: number;
	ticketsSold: number;
	upcomingEvents: number;
	platformFee: number;
	platformFeeRate: number;
	paymentFee: number;
	paymentFeeRate: number;
	netIncome: number;
}

export interface SaleTransaction {
	id: number;
	event_title: string;
	date: string | null;
	ticket_type: string;
	quantity: number;
	amount: number;
	buyer_name: string;
}

export interface SalesSeriesPoint {
	bucket: string;
	label: string;
	sales: number;
}

interface SalesSummaryResponse {
	summary?: Partial<SalesSummary> | null;
}

type SaleTransactionPayload = {
	id: number | string;
	event_title?: string | null;
	date?: string | null;
	ticket_type?: string | null;
	quantity?: number | string | null;
	amount?: number | string | null;
	buyer_name?: string | null;
};

interface SalesTransactionsResponse {
	transactions?: SaleTransactionPayload[] | null;
}

type SalesSeriesPayload = {
	bucket?: string | null;
	label?: string | null;
	month?: string | null;
	sales?: number | string | null;
};

interface SalesMonthlyResponse {
	months?: SalesSeriesPayload[] | null;
	series?: SalesSeriesPayload[] | null;
}

export const EMPTY_SALES_SUMMARY: SalesSummary = {
	totalSales: 0,
	monthlySales: 0,
	ticketsSold: 0,
	upcomingEvents: 0,
	platformFee: 0,
	platformFeeRate: 0,
	paymentFee: 0,
	paymentFeeRate: 0,
	netIncome: 0,
};

const toNumber = (value: unknown): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeSummary = (summary?: Partial<SalesSummary> | null): SalesSummary => {
	if (!summary) {
		return { ...EMPTY_SALES_SUMMARY };
	}

	return {
		totalSales: toNumber(summary.totalSales),
		monthlySales: toNumber(summary.monthlySales),
		ticketsSold: Math.max(0, Math.trunc(toNumber(summary.ticketsSold))),
		upcomingEvents: Math.max(0, Math.trunc(toNumber(summary.upcomingEvents))),
		platformFee: Math.max(0, Math.trunc(toNumber(summary.platformFee))),
		platformFeeRate: toNumber(summary.platformFeeRate),
		paymentFee: Math.max(0, Math.trunc(toNumber(summary.paymentFee))),
		paymentFeeRate: toNumber(summary.paymentFeeRate),
		netIncome: toNumber(summary.netIncome),
	};
};

const normalizeId = (value: number | string): number => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeTransactions = (
	transactions?: SaleTransactionPayload[] | null
): SaleTransaction[] => {
	if (!transactions || transactions.length === 0) {
		return [];
	}

	return transactions.map((transaction) => ({
		id: normalizeId(transaction.id),
		event_title: transaction.event_title ?? "未設定イベント",
		date: transaction.date ?? null,
		ticket_type: transaction.ticket_type ?? "",
		quantity: Math.max(0, Math.trunc(toNumber(transaction.quantity))),
		amount: toNumber(transaction.amount),
		buyer_name: transaction.buyer_name ?? "不明",
	}));
};

const sanitizeSeries = (months?: SalesSeriesPayload[] | null): SalesSeriesPoint[] => {
	if (!months || months.length === 0) {
		return [];
	}

	return months.map((month) => {
		const label = month.label ?? month.month ?? month.bucket ?? "";
		return {
			bucket: month.bucket ?? month.month ?? label,
			label,
			sales: toNumber(month.sales),
		};
	});
};

const buildQueryString = (params?: SalesQueryParams): string => {
	if (!params) {
		return "";
	}

	const search = new URLSearchParams();

	if (params.period) {
		search.set("period", params.period);
	}

	if (params.startDate) {
		search.set("start_date", params.startDate);
	}

	if (params.endDate) {
		search.set("end_date", params.endDate);
	}

	if (typeof params.limit === "number" && params.limit > 0) {
		search.set("limit", String(params.limit));
	}

	if (params.interval) {
		search.set("interval", params.interval);
	}

	const query = search.toString();
	return query ? `?${query}` : "";
};

export const fetchSalesSummary = async (
	params?: SalesQueryParams
): Promise<SalesSummary> => {
	const response = await api.get<SalesSummaryResponse>(
		`/organizer/sales/summary${buildQueryString(params)}`
	);
	return sanitizeSummary(response.summary);
};

export const fetchRecentSales = async (
	params?: SalesQueryParams
): Promise<SaleTransaction[]> => {
	const response = await api.get<SalesTransactionsResponse>(
		`/organizer/sales/recent${buildQueryString(params)}`
	);
	return sanitizeTransactions(response.transactions);
};

export const fetchSalesSeries = async (
	params?: SalesQueryParams
): Promise<SalesSeriesPoint[]> => {
	const response = await api.get<SalesMonthlyResponse>(
		`/organizer/sales/monthly${buildQueryString(params)}`
	);
	return sanitizeSeries(response.series ?? response.months);
};

export const fetchOrganizerSalesBundle = async (
	params?: SalesQueryParams
): Promise<{
	summary: SalesSummary;
	transactions: SaleTransaction[];
	months: SalesSeriesPoint[];
}> => {
	const [summary, transactions, months] = await Promise.all([
		fetchSalesSummary(params),
		fetchRecentSales(params),
		fetchSalesSeries(params),
	]);

	return { summary, transactions, months };
};
